import axios from 'axios'

// Base URL for the API
const API_BASE_URL = 

  process.env.REACT_APP_NEW_BASE_URL || 'https://staging-backend-v3-express.de.energy'


// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor to handle MongoDB ObjectId format
apiClient.interceptors.response.use(
  (response) => {
    // Transform response data to handle MongoDB ObjectId format
    if (response.data && typeof response.data === 'object') {
      response.data = transformMongoDBData(response.data)
    }
    return response
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Helper function to transform MongoDB data
const transformMongoDBData = (data) => {
  if (Array.isArray(data)) {
    return data.map(transformMongoDBData)
  } else if (data && typeof data === 'object') {
    // Handle MongoDB ObjectId format: { $oid: "..." }
    if (data.$oid) {
      return data.$oid
    }
    
    // Handle MongoDB Date format: { $date: "..." }
    if (data.$date) {
      return new Date(data.$date).toISOString()
    }
    
    // Handle MongoDB NumberLong format: { $numberLong: "..." }
    if (data.$numberLong) {
      return parseInt(data.$numberLong, 10)
    }
    
    // Recursively transform nested objects
    const transformed = {}
    for (const key in data) {
      transformed[key] = transformMongoDBData(data[key])
    }
    return transformed
  }
  return data
}

// Issue Service Functions
export const fetchIssues = async (filters = {}) => {
  try {
    const response = await apiClient.get('/api/issues-v2', { params: filters })
    return response.data.issues || []
  } catch (error) {
    console.error('Error fetching issues:', error)
    // Return mock data for development
    return getMockIssues()
  }
}

export const fetchIssueById = async (issueId) => {
  try {
    const response = await apiClient.get(`/api/issues-v2/${issueId}`)
    return response.data.issue
  } catch (error) {
    console.error('Error fetching issue:', error)
    throw error
  }
}

export const createIssue = async (issueData) => {
  const response = await fetch(`${API_BASE_URL}api/issues-v2`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(issueData),
  })

  if (!response.ok) {
    throw new Error('Failed to create issue')
  }

  const data = await response.json()
  // Return the full issue object which should include id or id
  return data.issue
}

export const updateIssue = async (issueId, issueData) => {
  try {
    const response = await apiClient.put(`/api/issues-v2/${issueId}`, issueData)
    return response.data.issue
  } catch (error) {
    console.error('Error updating issue:', error)
    throw error
  }
}

export const deleteIssue = async (issueId) => {
  try {
    const response = await apiClient.delete(`/api/issues-v2/${issueId}`)
    return response.data
  } catch (error) {
    console.error('Error deleting issue:', error)
    throw error
  }
}

export const addComment = async (issueId, commentData) => {
  try {
    const response = await apiClient.post(
      `/api/issues-v2/${issueId}/comments`,
      commentData
    )
    return response.data.comment
  } catch (error) {
    console.error('Error adding comment:', error)
    throw error
  }
}

export const updateIssueStatus = async (issueId, status) => {
  try {
    const response = await apiClient.patch(`/api/issues-v2/${issueId}/status`, {
      status,
    })
    return response.data.issue
  } catch (error) {
    console.error('Error updating issue status:', error)
    throw error
  }
}

export const assignIssue = async (issueId, assignedTo) => {
  try {
    const response = await apiClient.patch(`/api/issues-v2/${issueId}/assign`, {
      assignedTo,
    })
    return response.data.issue
  } catch (error) {
    console.error('Error assigning issue:', error)
    throw error
  }
}

// Fetch plant list for dropdowns
/* export const fetchPlantList = async () => {
  try {
    const response = await apiClient.get('/api/plants')
    return response.data.plants || []
  } catch (error) {
    console.error('Error fetching plants:', error)
    // Return mock data for development
    return getMockPlants()
  }
} */

export const fetchPlantListNew = async () => {
  try {
    const response = await apiClient.get('/api/allissueplants')
    const plants = response.data.plants || []

    return plants.map((plant) => ({
      ...plant,
      _id: plant._id,
      id: plant._id,
    }))
  } catch (error) {
    console.error('Error fetching plants:', error)
    // Return mock data for development
    return getMockPlants()
  }
}

// Fetch user list for assignment
export const fetchUserList = async () => {
  try {
    const response = await apiClient.get('/api/users?type=sub_admin&limit=10&offset=0')
    return response.data.data || []
  } catch (error) {
    console.error('Error fetching users:', error)
    // Return mock data for development
    return getMockUsers()
  }
}

// Upload attachment
export const uploadAttachment = async (issueId, file) => {
  try {
    console.log('Uploading attachment for issue:', issueId)
    console.log('File:', file)
    
    const formData = new FormData()
    formData.append('file', file)
    formData.append('issueId', issueId)

    const response = await apiClient.post('/api/issues/attachments', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    
    console.log('Upload response:', response.data)
    return response.data.attachment
  } catch (error) {
    console.error('Error uploading attachment:', error)
    console.error('Error details:', error.response?.data)
    throw error
  }
}

export const fetchIssueAttachment = async (issueId) => {
   try {
    const response = await apiClient.get(`/api/issues/${issueId}/attachments`)
    return response.data.attachments || []
  } catch (error) {
    console.error('Error fetching issue attachments:', error)
    throw error
  }
}

export const fetchFrequentTags = async (limit = 4) => {
  try {
    const response = await apiClient.get('/api/issues-v2/frequent-tags', {
      params: { limit }
    })
    return response.data.tags || []
  } catch (error) {
    console.error('Error fetching frequent tags:', error)
    return []
  }
}

// Mock data for development
const getMockIssues = () => [
  {
    id: '67a1b2c3d4e5f67890123456',
    title: 'Inverter 1 Performance Drop',
    description: 'Inverter 1 showing 15% drop in performance compared to expected output.',
    plantId: '67a1b2c3d4e5f67890123457',
    plantName: 'Solar Plant Alpha',
    category: 'Equipment Failure',
    priority: 'High',
    status: 'Open',
    assignedTo: 'user-1',
    createdAt: '2024-06-20T10:00:00Z',
    expectedClosingDate: '2024-06-25T10:00:00Z',
    tags: ['inverter', 'performance', 'urgent'],
    affectedEquipment: 'Inverter 1',
    severityImpact: 'High',
    createdBy: 'System Admin',
    comments: [
      {
        id: 1,
        text: 'Issue reported by monitoring system',
        author: 'System',
        createdAt: '2024-06-20T10:00:00Z',
        type: 'comment',
      },
    ],
  },
  {
    id: '2',
    issueId: 'ISS-002',
    title: 'Energy Meter Communication Error',
    description:
      'Energy meter 2 not responding to data requests. Communication timeout errors logged.',
    plantId: 'plant-1',
    plantName: 'Solar Plant Alpha',
    category: 'Sensor Issues',
    priority: 'Medium',
    status: 'In Progress',
    assignedTo: 'user-2',
    assignedToName: 'Jane Smith',
    createdAt: '2024-06-19T14:30:00Z',
    expectedClosingDate: '2024-06-24T14:30:00Z',
    tags: ['energy-meter', 'communication'],
    affectedEquipment: 'Energy Meter 2',
    severityImpact: 'Medium',
    createdBy: 'Tech Team',
    comments: [],
  },
  {
    id: '3',
    issueId: 'ISS-003',
    title: 'Scheduled Maintenance Required',
    description: 'Quarterly maintenance due for all inverters in Plant Beta.',
    plantId: 'plant-2',
    plantName: 'Solar Plant Beta',
    category: 'Maintenance Required',
    priority: 'Low',
    status: 'Open',
    assignedTo: null,
    assignedToName: null,
    createdAt: '2024-06-18T09:00:00Z',
    expectedClosingDate: '2024-06-30T09:00:00Z',
    tags: ['maintenance', 'scheduled'],
    affectedEquipment: 'All Inverters',
    severityImpact: 'Low',
    createdBy: 'Maintenance Team',
    comments: [],
  },
  {
    id: '4',
    issueId: 'ISS-004',
    title: 'Grid Connection Instability',
    description:
      'Frequent grid disconnections observed. Need to check grid parameters and connection stability.',
    plantId: 'plant-1',
    plantName: 'Solar Plant Alpha',
    category: 'Grid Issues',
    priority: 'High',
    status: 'Resolved',
    assignedTo: 'user-1',
    assignedToName: 'John Doe',
    createdAt: '2024-06-15T11:15:00Z',
    expectedClosingDate: '2024-06-20T11:15:00Z',
    resolvedAt: '2024-06-19T16:30:00Z',
    tags: ['grid', 'stability', 'connection'],
    affectedEquipment: 'Grid Connection',
    severityImpact: 'High',
    createdBy: 'Operations Team',
    comments: [
      {
        id: 1,
        text: 'Investigation started',
        author: 'John Doe',
        createdAt: '2024-06-15T12:00:00Z',
        type: 'comment',
      },
      {
        id: 2,
        text: 'Found loose connection in main panel',
        author: 'John Doe',
        createdAt: '2024-06-19T15:00:00Z',
        type: 'comment',
      },
      {
        id: 3,
        text: 'Connection repaired and tested',
        author: 'John Doe',
        createdAt: '2024-06-19T16:30:00Z',
        type: 'comment',
      },
    ],
  },
]

const getMockPlants = () => [
  {
    id: '67a1b2c3d4e5f67890123457',
    plantName: 'Solar Plant Alpha',
    projectCode: 'SPA-001',
    plantLocation: 'Location A',
  },
  {
    id: 'plant-2',
    plantName: 'Solar Plant Beta',
    projectCode: 'SPB-002',
    plantLocation: 'Location B',
  },
  {
    id: 'plant-3',
    plantName: 'Solar Plant Gamma',
    projectCode: 'SPG-003',
    plantLocation: 'Location C',
  },
]

const getMockUsers = () => [
  {
    userId: 'user-1',
    name: 'John Doe',
    email: 'john.doe@company.com',
    role: 'Senior Technician',
  },
  {
    id: 'user-2',
    name: 'Jane Smith',
    email: 'jane.smith@company.com',
    role: 'Operations Manager',
  },
  {
    id: 'user-3',
    name: 'Mike Johnson',
    email: 'mike.johnson@company.com',
    role: 'Maintenance Specialist',
  },
  {
    id: 'user-4',
    name: 'Sarah Wilson',
    email: 'sarah.wilson@company.com',
    role: 'System Administrator',
  },
]

export default {
  fetchIssues,
  fetchIssueById,
  createIssue,
  updateIssue,
  deleteIssue,
  addComment,
  updateIssueStatus,
  assignIssue,
  //fetchPlantList,
  fetchPlantListNew,
  fetchUserList,
  uploadAttachment,
  fetchIssueAttachment,
  fetchFrequentTags,
}

